import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Icons } from './Icons';

const EASE = [0.22, 1, 0.36, 1];
const SANS = '"Geist", "Inter", system-ui, sans-serif';
const MONO = '"Geist Mono", ui-monospace, monospace';
const GRAIN_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`;

const plans = {
  pro: {
    name: 'Pro',
    price: '$9.99',
    period: '/mo',
    color: '#a78bfa',
    accentBg: 'rgba(90,56,253,0.12)',
    accentBorder: 'rgba(90,56,253,0.35)',
    gradient: 'linear-gradient(135deg, #5A38FD 0%, #7F00FF 100%)',
    features: [
      '4 Contract Analyses / month',
      '8 Post Generations / month',
      'Ghost Agent (4 chats / month)',
      'Deal Safety Score™',
      'Social Media Risk Scanner',
      'NIL Earnings Tracker',
      'Email Support',
    ],
    popular: true,
  },
  elite: {
    name: 'Elite',
    price: '$21.99',
    period: '/mo',
    color: '#fbbf24',
    accentBg: 'rgba(245,158,11,0.08)',
    accentBorder: 'rgba(245,158,11,0.25)',
    gradient: 'linear-gradient(135deg, #7F00FF 0%, #5A38FD 50%, #3B82F6 100%)',
    features: [
      'Unlimited Contract Analyses',
      'Unlimited Caption Generations',
      'Advanced Ghost Agent (Unlimited)',
      'Deal Safety Score™ with trends',
      'Advanced Social Media Risk Scanner',
      'Priority Support',
      'Advanced NIL Analytics',
      'Market Value Assessment',
      'Deal Performance Tracking',
    ],
    popular: false,
  },
};

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="7" cy="7" r="7" fill="rgba(16,185,129,0.18)" />
    <path d="M4 7l2 2 4-4" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SubscriptionUpgradeModal = ({ isOpen, onClose, currentTier = 'free' }) => {
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const tier = currentTier.toLowerCase();
  const isPaid = tier !== 'free';
  const tierLabel = { free: 'Free', pro: 'Pro', elite: 'Elite' }[tier] || tier;
  const tierColor = { free: 'rgba(255,255,255,0.3)', pro: '#a78bfa', elite: '#fbbf24' }[tier] || '#fff';

  const handleUpgrade = async (planId) => {
    setLoading(true);
    try {
      const res = await apiCall('/api/stripe/create-subscription-checkout', {
        method: 'POST',
        body: JSON.stringify({ package_id: planId, origin_url: window.location.origin }),
      });
      if (res?.success && res?.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        throw new Error(res?.message || 'Failed to create checkout session');
      }
    } catch (err) {
      alert(`Upgrade failed: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleManagePortal = async () => {
    setLoading(true);
    try {
      const res = await apiCall('/api/stripe/customer-portal', {
        method: 'POST',
        body: JSON.stringify({ return_url: window.location.origin }),
      });
      if (res?.success && res?.portal_url) {
        window.location.href = res.portal_url;
      } else {
        throw new Error(res?.message || 'Failed to access customer portal');
      }
    } catch (err) {
      alert(`Could not open subscription portal: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @media (max-width: 600px) {
          .nv-plan-grid { grid-template-columns: 1fr !important; }
          .nv-features-grid { grid-template-columns: 1fr !important; }
          .nv-sub-modal-body { padding: 14px !important; }
          .nv-sub-modal-header { padding: 14px !important; }
        }
      `}</style>

      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          background: 'rgba(4,2,14,0.82)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
        }}
        onClick={onClose}
      />

      {/* Scroll container */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem', overflowY: 'auto',
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          onClick={e => e.stopPropagation()}
          style={{
            position: 'relative',
            background: '#0D0D16',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 22,
            width: '100%',
            maxWidth: isPaid ? 460 : 820,
            maxHeight: 'calc(100vh - 2rem)',
            overflowY: 'auto',
            boxShadow: '0 24px 80px rgba(0,0,0,0.85), 0 0 0 1px rgba(90,56,253,0.1)',
            fontFamily: SANS,
          }}
        >
          {/* Grain overlay */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 22,
            opacity: 0.04, mixBlendMode: 'overlay',
            backgroundImage: GRAIN_SVG, backgroundSize: '120px 120px',
            pointerEvents: 'none', zIndex: 0,
          }} />

          {/* Top hairline */}
          <div style={{
            position: 'absolute', top: 0, left: '15%', right: '15%', height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(90,56,253,0.55) 40%, rgba(167,139,250,0.65) 60%, transparent)',
            pointerEvents: 'none', zIndex: 1,
          }} />

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 2 }}>

            {/* Header */}
            <div className="nv-sub-modal-header" style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              padding: '20px 20px 16px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: 'linear-gradient(135deg, #5A38FD, #7F00FF)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 0 16px rgba(90,56,253,0.35)',
                }}>
                  <Icons.Crown style={{ width: 16, height: 16, color: '#fff' }} />
                </div>
                <div>
                  <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.2 }}>
                    {isPaid ? 'Subscription Settings' : 'Upgrade Your Plan'}
                  </h2>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.38)', margin: '3px 0 0' }}>
                    {isPaid
                      ? `You're currently on the ${tierLabel} plan`
                      : 'Unlock advanced NIL features and maximize your earning potential'}
                  </p>
                </div>
              </div>
              <motion.button
                whileTap={{ scale: 0.92 }}
                onClick={onClose}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, cursor: 'pointer', padding: 6,
                  color: 'rgba(255,255,255,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icons.Close style={{ width: 16, height: 16 }} />
              </motion.button>
            </div>

            {/* Body */}
            <div className="nv-sub-modal-body" style={{ padding: '20px' }}>

              {isPaid ? (
                /* ── Paid user view ── */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                  {/* Current plan card */}
                  <div style={{
                    background: 'rgba(90,56,253,0.07)',
                    border: '1px solid rgba(90,56,253,0.2)',
                    borderRadius: 14, padding: '16px 18px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <Icons.Crown style={{ width: 18, height: 18, color: tierColor }} />
                      <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff' }}>{tierLabel} Plan</span>
                      <span style={{
                        padding: '2px 8px',
                        background: 'rgba(16,185,129,0.14)',
                        border: '1px solid rgba(16,185,129,0.22)',
                        borderRadius: 999,
                        fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.07em',
                        color: '#34d399',
                      }}>ACTIVE</span>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.38)', margin: 0, fontFamily: MONO }}>
                      {tier === 'pro' ? '$9.99 / month' : '$21.99 / month'} · Billed monthly
                    </p>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {tier === 'pro' && (
                      <motion.button
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleUpgrade('elite')}
                        disabled={loading}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '14px 18px', width: '100%',
                          background: 'linear-gradient(135deg, rgba(90,56,253,0.22), rgba(127,0,255,0.22))',
                          border: '1px solid rgba(90,56,253,0.35)',
                          borderRadius: 12, cursor: loading ? 'not-allowed' : 'pointer',
                          opacity: loading ? 0.65 : 1, fontFamily: SANS,
                        }}
                      >
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem', marginBottom: 2 }}>Upgrade to Elite</div>
                          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)' }}>Unlock unlimited features · $21.99/mo</div>
                        </div>
                        <Icons.TrendUp style={{ width: 18, height: 18, color: '#a78bfa', flexShrink: 0 }} />
                      </motion.button>
                    )}

                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      onClick={handleManagePortal}
                      disabled={loading}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '14px 18px', width: '100%',
                        background: '#141421',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 12, cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.65 : 1, fontFamily: SANS,
                      }}
                    >
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem', marginBottom: 2 }}>Manage Subscription</div>
                        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.38)' }}>Cancel, pause, or modify your plan</div>
                      </div>
                      <Icons.Settings style={{ width: 18, height: 18, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
                    </motion.button>
                  </div>

                  {/* Plan features */}
                  <div style={{
                    background: '#0F0F17',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 12, padding: '14px 16px',
                  }}>
                    <p style={{
                      fontSize: '0.6rem', fontWeight: 800, color: 'rgba(255,255,255,0.2)',
                      textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px',
                    }}>Your plan includes</p>
                    <div className="nv-features-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {(plans[tier] || plans.pro).features.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                          <CheckIcon />
                          <span style={{ fontSize: '0.775rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.4 }}>{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

              ) : (
                /* ── Free user: upgrade plans ── */
                <div>
                  <motion.div
                    className="nv-plan-grid"
                    variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
                    initial="hidden" animate="show"
                    style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}
                  >
                    {Object.entries(plans).map(([id, plan]) => (
                      <motion.div
                        key={id}
                        variants={{
                          hidden: { opacity: 0, y: 16 },
                          show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 340, damping: 28 } },
                        }}
                        whileHover={{ y: -2, transition: { duration: 0.14 } }}
                        style={{
                          position: 'relative',
                          background: plan.popular ? plan.accentBg : '#0F0F17',
                          border: `1px solid ${plan.popular ? plan.accentBorder : 'rgba(255,255,255,0.08)'}`,
                          borderRadius: 16, padding: '20px 18px',
                          display: 'flex', flexDirection: 'column',
                        }}
                      >
                        {plan.popular && (
                          <div style={{
                            position: 'absolute', top: -1, left: '50%', transform: 'translateX(-50%)',
                            background: 'linear-gradient(135deg, #5A38FD, #7F00FF)',
                            borderRadius: '0 0 8px 8px',
                            padding: '3px 12px',
                            fontSize: '0.58rem', fontWeight: 800, color: '#fff',
                            letterSpacing: '0.09em', whiteSpace: 'nowrap', fontFamily: MONO,
                          }}>MOST POPULAR</div>
                        )}

                        {/* Plan name & price */}
                        <div style={{ marginBottom: 16, paddingTop: plan.popular ? 8 : 0 }}>
                          <div style={{
                            fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.09em',
                            color: plan.color, textTransform: 'uppercase', marginBottom: 8, fontFamily: MONO,
                          }}>{plan.name}</div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                            <span style={{ fontSize: '1.9rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em', fontFamily: MONO }}>
                              {plan.price}
                            </span>
                            <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)' }}>{plan.period}</span>
                          </div>
                        </div>

                        {/* Features */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, marginBottom: 18 }}>
                          {plan.features.map((f, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                              <CheckIcon />
                              <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.4 }}>{f}</span>
                            </div>
                          ))}
                        </div>

                        {/* CTA */}
                        <motion.button
                          whileTap={{ scale: 0.96 }}
                          onClick={() => handleUpgrade(id)}
                          disabled={loading}
                          style={{
                            width: '100%', padding: '11px 0',
                            borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer',
                            fontWeight: 700, fontSize: '0.875rem', color: '#fff', fontFamily: SANS,
                            background: plan.popular
                              ? 'linear-gradient(135deg, #5A38FD, #7F00FF)'
                              : 'rgba(255,255,255,0.07)',
                            border: plan.popular ? 'none' : '1px solid rgba(255,255,255,0.1)',
                            opacity: loading ? 0.65 : 1,
                            boxShadow: plan.popular ? '0 0 20px rgba(90,56,253,0.3)' : 'none',
                          }}
                        >
                          {loading ? 'Processing…' : `Choose ${plan.name}`}
                        </motion.button>
                      </motion.div>
                    ))}
                  </motion.div>

                  <p style={{
                    textAlign: 'center', fontSize: '0.72rem',
                    color: 'rgba(255,255,255,0.2)', marginTop: 14, fontFamily: MONO,
                  }}>
                    Cancel anytime · Billed monthly via Stripe
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default SubscriptionUpgradeModal;
